/**
 * Audit logging hook.
 * 
 * @module hooks/useAuditLog
 */

import { useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import type { AuditActionType, AuditResourceType } from '../lib/audit';

/**
 * Hook for logging admin actions to the audit trail.
 * 
 * @returns Audit logging function
 * 
 * @example
 * const { logAction } = useAuditLog();
 * 
 * // Log viewing a user
 * logAction('view_user', 'user', userId, { email: user.email });
 */
export function useAuditLog() {
  const { adminUser } = useAuth();

  /**
   * Logs an admin action to the audit trail.
   * 
   * @param actionType - Type of action performed
   * @param resourceType - Type of resource accessed
   * @param resourceId - ID of the resource accessed
   * @param resourceDetails - Additional context about the resource
   */
  const logAction = useCallback(
    async (
      actionType: AuditActionType,
      resourceType?: AuditResourceType,
      resourceId?: string,
      resourceDetails?: Record<string, unknown>
    ) => {
      if (!adminUser) {
        console.warn('Cannot log audit action: no admin user');
        return;
      }

      try {
        const { error } = await supabase.from('admin_audit_logs').insert({
          admin_user_id: adminUser.id,
          admin_email: adminUser.email,
          action_type: actionType,
          resource_type: resourceType ?? null,
          resource_id: resourceId ?? null,
          resource_details: resourceDetails ?? null,
          user_agent: navigator.userAgent,
          // Note: IP address would need server-side capture
          // For now we leave it null
          ip_address: null,
        } as never);

        if (error) {
          console.error('Failed to log audit action:', error);
        }
      } catch (err) {
        console.error('Audit logging error:', err);
        // Don't throw - audit logging should not break functionality
      }
    },
    [adminUser]
  );

  return { logAction };
}
